import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

type ChartData = {
  name: string;
  value: number;
};

interface PieChartComponentProps {
  title: string;
  data: ChartData[];
  colordata: string[];
  innerRadius?: number;
  outerRadius?: number;
}

const INNER_RADIUS = 90;
const OUTER_RADIUS = 140;

// Labels inside slices
const renderCustomizedLabel = ({ cx, cy, midAngle, value }: any) => {
  const RADIAN = Math.PI / 180;

  const radius = INNER_RADIUS + (OUTER_RADIUS - INNER_RADIUS) * 0.5;

  const x = cx + radius * Math.cos(-midAngle * RADIAN);

  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="#FFFFFF"
      textAnchor="middle"
      dominantBaseline="central"
      fontSize={15}
      fontWeight={700}
    >
      {value}
    </text>
  );
};

// Tooltip
const CustomTooltip = ({
  active,
  payload,
  colordata,
  data,
}: {
  active?: boolean;
  payload?: any;
  colordata: string[];
  data: ChartData[];
}) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const { name, value } = payload[0].payload;

  const index = data.findIndex(item => item.name === name);

  return (
    <div
      className="rounded shadow"
      style={{
        padding: "8px 12px",
        color: "#fff",
        backgroundColor: `#${colordata[index % colordata.length]}`,
      }}
    >
      <div className="font-bold">{name}</div>
      <div>{value.toLocaleString()}</div>
    </div>
  );
};

const PieCharts: React.FC<PieChartComponentProps> = ({
  title,
  data,
  colordata,
  innerRadius = INNER_RADIUS,
  outerRadius = OUTER_RADIUS,
}) => {
  return (
    <div className="w-full bg-white rounded-xl border shadow ">
      {/* Header */}
      <h2 className="text-sm font-semibold text-gray-600 p-4">{title}</h2>

      <div
        className="border-b border-gray-300 mx-auto "
        style={{ width: "95%" }}
      />

      {/* Content */}
      <div className="flex items-center justify-center flex-wrap px-5">
        {/* Half Donut */}
        <div
          style={{
            width: "55%",
            minWidth: "340px",
            height: "280px",
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                startAngle={180}
                endAngle={0}
                cx="50%"
                cy="75%"
                innerRadius={innerRadius}
                outerRadius={outerRadius}
                paddingAngle={2}
                labelLine={false}
                label={renderCustomizedLabel}
              >
                {data.map((_, index) => (
                  <Cell
                    key={index}
                    fill={`#${colordata[index % colordata.length]}`}
                  />
                ))}
              </Pie>

              <Tooltip
                content={<CustomTooltip data={data} colordata={colordata} />}
              />

              <Legend
                verticalAlign="bottom"
                align="center"
                iconType="rect"
                iconSize={10}
                wrapperStyle={{
                  transform: "translateY(-45px)", // adjust between -30 and -60
                  fontSize: "13px",
                  fontWeight: 500,
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default PieCharts;

interface GaugeChartProps {
  title: string;
  data: ChartData[];
  colordata: string[];
  innerRadius?: number;
  outerRadius?: number;
}

export const GaugeChart: React.FC<GaugeChartProps> = ({
  title,
  data,
  colordata,
  innerRadius = 112,
  outerRadius = 134,
}) => {
  if (!data.length) return null;

  const value = data[0].value;
  const label = data[0].name;

  const maxValue = 180;

  const progress = Math.min(value / maxValue, 1);

  const gaugeData = [
    {
      name: "Progress",
      value: progress * 100,
    },
    {
      name: "Remaining",
      value: 100 - progress * 100,
    },
  ];

  const RADIAN = Math.PI / 180;

  const radius = (innerRadius + outerRadius) / 2;

  const angle = 180 - progress * 180;

  const cx = 210;
  const cy = 186;

  const dotX = cx + radius * Math.cos(-angle * RADIAN);
  const dotY = cy + radius * Math.sin(-angle * RADIAN);

  const CustomTooltip = ({ active }: any) => {
    if (!active) return null;

    return (
      <div
        style={{
          background: `#${colordata[0]}`,
          color: "#fff",
          padding: "8px 12px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
        }}
      >
        <div>{label}</div>
        <div>{value}</div>
      </div>
    );
  };

  return (
    <div className="w-full bg-white rounded-xl border shadow">
      <h2 className="text-sm font-semibold text-gray-600 p-4">{title}</h2>

      <div
        className="border-b border-gray-300 mx-auto"
        style={{ width: "95%" }}
      />

      <div
        style={{
          width: "100%",
          height: 260,
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Background */}
            <Pie
              data={[{ value: 100 }]}
              dataKey="value"
              startAngle={180}
              endAngle={0}
              cx="50%"
              cy="78%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              stroke="none"
              cornerRadius={10}
            >
              <Cell fill="#FFBB10" />
            </Pie>

            {/* Progress */}
            <Pie
              data={gaugeData}
              dataKey="value"
              startAngle={180}
              endAngle={0}
              cx="50%"
              cy="78%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              stroke="none"
              cornerRadius={10}
            >
              <Cell fill={`#${colordata[0]}`} stroke="none" />
              <Cell fill="transparent" stroke="none" />
            </Pie>

            {/* Thumb */}
            <circle
              cx={dotX}
              cy={dotY}
              r={16}
              fill={`#${colordata[0]}`}
              stroke="#fff"
              strokeWidth={4}
              style={{
                filter: "drop-shadow(0 2px 6px rgba(0,0,0,.18))",
              }}
            />

            {/* Value */}
            <text
              x="50%"
              y="57%"
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="25"
              fontWeight="700"
              fill="#222"
            >
              {value}
            </text>

            <Tooltip content={<CustomTooltip />} />

            <Legend
              verticalAlign="bottom"
              align="center"
              iconType="rect"
              iconSize={10}
              payload={[
                {
                  value: label,
                  type: "rect",
                  color: `#${colordata[0]}`,
                },
              ]}
              wrapperStyle={{
                transform: "translateY(-30px)",
                fontSize: "13px",
                fontWeight: 500,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
